import * as React from "react";
import {
  ArrowRight,
  ChevronRight,
  Loader2,
  PauseCircle,
  Play,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  useContinueProjectMutation,
  useProjectsListQuery,
} from "@/entities/projects/queries";
import type {
  ProjectListItem,
  ProjectStatus,
} from "@/entities/projects/types";
import { ApiError } from "@/shared/api/http";
import { cn } from "@/shared/lib/cn";
import { formatNumber, formatTimeAgo } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

const STATUS_LABEL: Record<ProjectStatus, { label: string; class: string }> = {
  queued:     { label: "В очереди",      class: "bg-amber/15 text-amber border-amber/35" },
  collecting: { label: "Сбор",           class: "bg-cyan/15 text-cyan border-cyan/35" },
  clustering: { label: "Кластеризация",  class: "bg-purple/15 text-purple border-purple/35" },
  done:       { label: "Готово",         class: "bg-green/15 text-green border-green/35" },
  partial:    { label: "Частично",       class: "bg-amber/15 text-amber border-amber/35" },
  failed:     { label: "Ошибка",         class: "bg-pink/15 text-pink border-pink/35" },
  cancelled:  { label: "Отменено",       class: "bg-white/[0.05] text-muted-foreground border-white/15" },
};

const ACTIVE = new Set<ProjectStatus>(["queued", "collecting", "clustering"]);

export interface ProjectsListProps {
  archived: boolean | null;
  q: string | null;
}

export function ProjectsList({ archived, q }: ProjectsListProps): JSX.Element {
  const listQ = useProjectsListQuery({
    archived,
    q,
    limit: 50,
    offset: 0,
  });
  // Если есть активные — поллим каждые 10 секунд.
  const hasActive = (listQ.data?.items ?? []).some((p) => ACTIVE.has(p.status));

  // Если есть активные проекты — раз в 10 секунд тянем список,
  // чтобы прогресс шевелился даже без захода на конкретный проект.
  useAutoRefresh(() => listQ.refetch(), hasActive ? 10_000 : null);

  if (listQ.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (listQ.isError) {
    return (
      <div className="glass-card p-5 text-sm text-pink">
        Не удалось загрузить список проектов.{" "}
        <button
          type="button"
          onClick={() => listQ.refetch()}
          className="ml-2 underline hover:text-white"
        >
          Повторить
        </button>
      </div>
    );
  }

  const items = listQ.data?.items ?? [];
  if (items.length === 0) {
    return <EmptyState archived={archived} q={q} />;
  }

  return (
    <div className="space-y-3">
      {items.map((p) => (
        <ProjectCard key={p.id} project={p} />
      ))}
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectListItem }): JSX.Element {
  const navigate = useNavigate();
  const continueM = useContinueProjectMutation();

  const status = STATUS_LABEL[project.status];
  const isActive = ACTIVE.has(project.status);
  const canContinue =
    project.status === "partial" || project.status === "failed";
  const cost = Number(project.cost_rub);

  return (
    <button
      type="button"
      onClick={() => navigate(`/projects/${project.id}`)}
      className="group flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition-colors hover:border-white/25 hover:bg-white/[0.06]"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-baseline gap-2">
          <h3 className="truncate text-base font-bold text-white">
            {project.name}
          </h3>
          <span
            className={cn(
              "rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
              status.class,
            )}
          >
            {status.label}
          </span>
          {project.is_archived && (
            <span className="rounded-md border border-white/15 bg-white/[0.05] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              в архиве
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>создан {formatTimeAgo(project.created_at)}</span>
          {isActive && project.progress > 0 && (
            <>
              <span>·</span>
              <span className="text-cyan">{project.progress}% выполнено</span>
            </>
          )}
          {project.requests_used > 0 && (
            <>
              <span>·</span>
              <span>{formatNumber(project.requests_used)} запросов</span>
            </>
          )}
          {cost > 0 && (
            <>
              <span>·</span>
              <span className="text-cyan">{cost.toFixed(2)} ₽</span>
            </>
          )}
        </div>

        {isActive && (
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/[0.06]">
            <div
              className="h-full bg-gradient-to-r from-purple to-cyan transition-all"
              style={{ width: `${Math.min(100, Math.max(0, project.progress))}%` }}
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {canContinue && (
          <Button
            type="button"
            size="sm"
            className="bg-gradient-primary text-white shadow-glow-purple"
            onClick={(e) => {
              e.stopPropagation();
              continueM.mutate(project.id, {
                onSuccess: () => toast.success("Сбор возобновлён"),
                onError: (err) =>
                  toast.error(
                    err instanceof ApiError ? err.message : "Не удалось",
                  ),
              });
            }}
            disabled={continueM.isPending}
          >
            {continueM.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            Продолжить
          </Button>
        )}
        {isActive && (
          <PauseCircle className="h-5 w-5 animate-soft-pulse text-cyan" />
        )}
        <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
      </div>
    </button>
  );
}

function EmptyState({
  archived,
  q,
}: {
  archived: boolean | null;
  q: string | null;
}): JSX.Element {
  if (q) {
    return (
      <div className="glass-card p-8 text-center text-sm text-muted-foreground">
        Ничего не нашли по запросу <b className="text-white">«{q}»</b>.
      </div>
    );
  }
  if (archived === true) {
    return (
      <div className="glass-card p-8 text-center text-sm text-muted-foreground">
        В архиве пока пусто.
      </div>
    );
  }
  return (
    <div className="glass-card p-10 text-center">
      <div className="mb-2 text-lg font-semibold text-white">
        У вас пока нет проектов
      </div>
      <p className="text-sm text-muted-foreground">
        Соберите первое семантическое ядро — это займёт несколько минут.
      </p>
      <a
        href="/projects/new"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-primary px-5 py-2.5 text-sm font-semibold text-white shadow-glow-purple hover:-translate-y-0.5"
      >
        Создать первый проект <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );
}

// --- helper: auto-refetch interval ---

function useAutoRefresh(fn: () => void, ms: number | null): void {
  React.useEffect(() => {
    if (ms === null) return;
    const t = setInterval(fn, ms);
    return () => clearInterval(t);
  }, [fn, ms]);
}
